import { observer } from 'mobx-react-lite';
import React from 'react';
import type { ValueType } from 'react-select';

import type { Billable } from '@feathr/blackbox';
import { Button, Input, Toolbar } from '@feathr/components';
import { useToggle } from '@feathr/hooks';

import BillableCard from '../BillableCard';
import BillableSelect from '../BillableSelect';

import * as styles from './BillableWidget.css';

interface IProps {
  isLoading?: boolean;
  onAdd?: () => void;
  onChange?: (value: ValueType<Billable>, refId?: string) => void;
  onSave?: () => void;
  redirect?: string;
  value?: Billable;
  refId?: string;
  allowEdit?: boolean;
}

function BillableWidget({
  allowEdit = true,
  isLoading = false,
  onAdd,
  onChange,
  onSave,
  redirect,
  refId,
  value,
}: IProps) {
  const [isChangeBillingVisible, toggleChangeBillingVisible] = useToggle(!value);

  function handleChange(changedValue: ValueType<Billable>) {
    if (onChange) {
      onChange(changedValue, refId);
    }
  }

  function handleRefIdChange(newValue?: string) {
    if (onChange) {
      onChange(value, newValue || undefined);
    }
  }

  function handleSave() {
    if (onSave) {
      onSave();
    }
    toggleChangeBillingVisible();
  }

  return !!value && !isChangeBillingVisible ? (
    <>
      <BillableCard
        allowEdit={allowEdit}
        billable={value}
        className={styles.billableCard}
        isLoading={value.isPending}
        redirect={redirect}
      />
    </>
  ) : (
    <>
      <div className={styles.inline}>
        <BillableSelect
          isLoading={isLoading}
          label={'Choose Billing Configuration'}
          onChange={handleChange}
          value={value}
        />
        {!!onAdd && <Button onClick={onAdd}>Create new billing configuration</Button>}
      </div>
      <Input
        helpText={
          'Optional reference identification field (such as a PO number) that will be included on media invoices for this project.'
        }
        label={'Reference ID'}
        onChange={handleRefIdChange}
        optional={true}
        type={'text'}
        value={refId}
      />
      <Toolbar>
        <Button onClick={toggleChangeBillingVisible} type={'naked'}>
          Cancel
        </Button>
        <Button onClick={handleSave} type={'success'}>
          Save
        </Button>
      </Toolbar>
    </>
  );
}

export default observer(BillableWidget);
